// src/pages/service-configuration/components/ServiceFilters.jsx
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";

const ServiceFilters = ({
  searchTerm,
  onSearchChange,
  categories = [],
  categoryFilter,
  onCategoryChange,
  statusFilter,
  onStatusChange,
  onClearFilters,
}) => {
  const { t } = useTranslation();

  const categoryOptions = useMemo(() => {
    const unique = Array.from(new Set((categories || []).map((c) => String(c || "").trim()).filter(Boolean)));
    unique.sort((a, b) => a.localeCompare(b, "ro"));
    return [{ value: "", label: t("services.filterAllCategories") }, ...unique.map((c) => ({ value: c, label: c }))];
  }, [categories, t]);

  const statusOptions = [
    { value: "active", label: t("services.filterOnlyActive") },
    { value: "inactive", label: t("services.filterOnlyInactive") },
    { value: "all", label: t("services.filterAll") },
  ];

  const hasActiveFilters = Boolean(searchTerm || categoryFilter || (statusFilter && statusFilter !== "active"));

  const categoryLabel = categoryOptions?.find((c) => c?.value === categoryFilter)?.label;
  const statusLabel = statusOptions?.find((s) => s?.value === statusFilter)?.label;

  return (
    <div className="bg-background border border-border rounded-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">{t("services.filterTitle")}</h3>
        {hasActiveFilters && (
          <Button variant="outline" size="sm" onClick={onClearFilters}>
            <Icon name="X" size={16} className="mr-2" />
            {t("services.clearFilters")}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="relative">
          <Input
            type="text"
            placeholder={t("services.filterSearchPlaceholder")}
            value={searchTerm}
            onChange={(e) => onSearchChange(e?.target?.value)}
            className="pl-10"
          />
          <Icon
            name="Search"
            size={18}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary"
          />
        </div>

        <Select
          placeholder={t("services.filterByCategory")}
          options={categoryOptions}
          value={categoryFilter}
          onChange={onCategoryChange}
          searchable
          clearable
        />

        <Select
          placeholder={t("services.filterByStatus")}
          options={statusOptions}
          value={statusFilter}
          onChange={onStatusChange}
        />
      </div>

      {hasActiveFilters && (
        <div className="flex items-center space-x-2 mt-4 pt-4 border-t border-border">
          <Icon name="Filter" size={16} className="text-text-secondary" />
          <span className="text-sm text-text-secondary">
            {t("services.activeFilters")}
            {searchTerm && (
              <span className="ml-1 px-2 py-1 bg-primary/10 text-primary rounded text-xs">
                &quot;{searchTerm}&quot;
              </span>
            )}
            {categoryFilter && (
              <span className="ml-1 px-2 py-1 bg-accent/10 text-accent rounded text-xs">
                {categoryLabel || categoryFilter}
              </span>
            )}
            {statusFilter && statusFilter !== "active" && (
              <span className="ml-1 px-2 py-1 bg-secondary/10 text-secondary rounded text-xs">
                {statusLabel || statusFilter}
              </span>
            )}
          </span>
        </div>
      )}
    </div>
  );
};

export default ServiceFilters;
